/**
 * OverlayQueue — central coordinator for onboarding overlays.
 *
 * Enforces strict mutual exclusion and priority order:
 *   1. category_onboarding  (priority 1)
 *   2. main_tour            (priority 2)
 *   3. tab_tour_<key>       (priority 3)
 *   4. biometric            (priority 4)
 *
 * Rules:
 *  - Only ONE overlay is active at any time (`active`).
 *  - When a component wants to show, it calls `acquire(id, priority)` and
 *    awaits the returned promise. It is only allowed to render the overlay
 *    AFTER the promise resolves.
 *  - When the component finishes / skips / closes, it MUST call `release(id)`.
 *  - A small GRACE window lets multiple components that mount near the same
 *    time collect their intents before we pick the highest priority one.
 *  - If a component declines to show (condition fails), it just never calls
 *    acquire, so the queue is not polluted.
 *
 * No React dependency — this is a plain JS singleton so it survives
 * rerenders, route changes and component lifecycles.
 */

let active = null;                  // id of overlay currently visible (or null)
const pending = [];                 // [{ id, priority, resolve }]
let graceTimer = null;
const GRACE_MS = 1200;              // first flush waits for concurrent requests

function _flushNext() {
  if (active) return;
  if (pending.length === 0) return;
  pending.sort((a, b) => a.priority - b.priority);
  const next = pending.shift();
  active = next.id;
  try {
    window.dispatchEvent(new CustomEvent('sv_overlay_active', { detail: { id: next.id } }));
  } catch { /* noop */ }
  try { next.resolve(); } catch { /* noop */ }
}

function _scheduleGraceFlush() {
  if (active) return;
  if (pending.length === 0) return;
  if (graceTimer) return;
  graceTimer = setTimeout(() => {
    graceTimer = null;
    _flushNext();
  }, GRACE_MS);
}

export const OverlayQueue = {
  /**
   * Request a slot. Returns a Promise that resolves when this overlay is
   * cleared to show. Safe to call multiple times with the same id.
   */
  acquire(id, priority) {
    if (active === id) return Promise.resolve();
    // Already queued? chain a new resolver onto existing entry.
    const existing = pending.find(p => p.id === id);
    if (existing) {
      return new Promise(resolve => {
        const originalResolve = existing.resolve;
        existing.resolve = () => { try { originalResolve(); } catch {} resolve(); };
      });
    }
    return new Promise(resolve => {
      pending.push({ id, priority, resolve });
      _scheduleGraceFlush();
    });
  },

  /**
   * Mark this overlay finished. The next-highest-priority pending overlay
   * (if any) activates immediately — no grace period.
   */
  release(id) {
    if (active === id) {
      active = null;
      try {
        window.dispatchEvent(new CustomEvent('sv_overlay_released', { detail: { id } }));
      } catch { /* noop */ }
      _flushNext();
    } else {
      const idx = pending.findIndex(p => p.id === id);
      if (idx >= 0) pending.splice(idx, 1);
    }
  },

  isActive(id) { return active === id; },
  isBusy() { return active !== null; },
  getActive() { return active; },

  /** Dev helper. */
  _state() { return { active, pending: pending.map(p => ({ id: p.id, priority: p.priority })) }; },
};

// Expose for DevTools / debugging only
if (typeof window !== 'undefined') {
  window.__sv_overlay_queue = OverlayQueue;
}

export const OVERLAY_PRIORITY = {
  CATEGORY: 1,
  MAIN_TOUR: 2,
  TAB_TOUR: 3,
  BIOMETRIC: 4,
};
